import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { of } from 'rxjs';
import { catchError, concatMap, map, switchMap, withLatestFrom } from 'rxjs/operators';

import { DomainsService } from '../../../core/services/domains.service';
import * as AdminDomainsActions from './admin-domains.actions';
import {
  selectAdminDomainsLastQuery,
  selectAdminDomainsMeta,
} from './admin-domains.selectors';
import {
  DEFAULT_ADMIN_DOMAINS_CURRENT_PAGE,
  DEFAULT_ADMIN_DOMAINS_PER_PAGE,
} from '../models/admin-domain.model';

@Injectable()
export class AdminDomainsEffects {
  private actions$ = inject(Actions);
  private domainsService = inject(DomainsService);
  private store = inject(Store);

  loadAdminDomains$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AdminDomainsActions.loadAdminDomains),
      switchMap((action) =>
        this.domainsService
          .getDomainsPage({
            page: action.page ?? DEFAULT_ADMIN_DOMAINS_CURRENT_PAGE,
            perPage: action.perPage ?? DEFAULT_ADMIN_DOMAINS_PER_PAGE,
            search: action.search ?? '',
            filters: action.filters,
          })
          .pipe(
            map((response) =>
              AdminDomainsActions.loadAdminDomainsSuccess({
                domains: response.data,
                meta: response.meta,
              }),
            ),
            catchError((error) =>
              of(
                AdminDomainsActions.loadAdminDomainsFailure({
                  error: error?.message ?? 'Failed to load domains',
                }),
              ),
            ),
          ),
      ),
    ),
  );

  loadAdminDomainDetail$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AdminDomainsActions.loadAdminDomainDetail),
      switchMap((action) =>
        this.domainsService.getDomainInformation(action.domainUuid).pipe(
          map((response) =>
            AdminDomainsActions.loadAdminDomainDetailSuccess({
              domain: response.data,
            }),
          ),
          catchError((error) =>
            of(
              AdminDomainsActions.loadAdminDomainDetailFailure({
                error: error?.message ?? 'Failed to load domain details',
              }),
            ),
          ),
        ),
      ),
    ),
  );

  updateDomainSettings$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AdminDomainsActions.updateDomainSettings),
      switchMap((action) =>
        this.domainsService
          .updateDomainSettings(action.domainUuid, action.payload)
          .pipe(
            map(() => AdminDomainsActions.updateDomainSettingsSuccess()),
            catchError((error) =>
              of(
                AdminDomainsActions.updateDomainSettingsFailure({
                  error: error?.message ?? 'Failed to update domain settings',
                }),
              ),
            ),
          ),
      ),
    ),
  );

  reloadAfterMutation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AdminDomainsActions.updateDomainSettingsSuccess),
      // Read the latest store state lazily, per emitted action, to avoid the
      // race condition where `withLatestFrom` subscribes to the selectors
      // eagerly (equivalent to @ngrx/operators `concatLatestFrom`).
      concatMap((action) =>
        of(action).pipe(
          withLatestFrom(
            this.store.select(selectAdminDomainsMeta),
            this.store.select(selectAdminDomainsLastQuery),
          ),
        ),
      ),
      map(([, meta, lastQuery]) =>
        AdminDomainsActions.loadAdminDomains({
          page: meta?.currentPage ?? DEFAULT_ADMIN_DOMAINS_CURRENT_PAGE,
          perPage: meta?.perPage ?? DEFAULT_ADMIN_DOMAINS_PER_PAGE,
          search: lastQuery.search,
          filters: lastQuery.filters,
          filterLink: lastQuery.filterLink,
        }),
      ),
    ),
  );
}
